import { useEffect, useRef, useState } from "react";
import { Drawer } from "vaul";
import { BrowserQRCodeReader } from "@zxing/browser";
import { haptic } from "./haptics";

/**
 * Modal QR scanner. Opens a fullscreen vaul Drawer with the device's
 * back camera, decodes QR codes in real-time, and fires `onDecode` once
 * with the decoded text. Auto-stops on success or unmount.
 *
 * Decoded payloads we expect:
 *   - Bare Zcash UA (`u1…`), zs1…, t1…
 *   - ZIP-321 URI (`zcash:u1…?amount=&memo=`)
 *   - Bot's UA paste-into-chat link (handled upstream)
 *
 * The caller is responsible for parsing / validating; we just hand
 * back the raw string.
 */
export function QrScanner({
  open,
  onClose,
  onDecode,
}: {
  open: boolean;
  onClose: () => void;
  onDecode: (text: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<{ stop: () => void } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const reader = new BrowserQRCodeReader();
        // Prefer the rear camera if multiple exist — first try the
        // facingMode constraint via device list, then fall back to
        // whichever camera the browser picks.
        const devices = await BrowserQRCodeReader.listVideoInputDevices();
        const rear =
          devices.find((d) => /back|rear|environment/i.test(d.label)) ??
          devices[devices.length - 1] ?? // last is often the rear cam
          null;
        if (cancelled || !videoRef.current) return;
        const controls = await reader.decodeFromVideoDevice(
          rear?.deviceId ?? undefined,
          videoRef.current,
          (result, _err, ctrl) => {
            if (cancelled) return;
            if (result) {
              ctrl.stop();
              haptic("success");
              onDecode(result.getText());
            }
          },
        );
        controlsRef.current = controls;
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        // Common shapes — camera permission denied, no camera, https
        // required.
        if (/Permission|NotAllowed/i.test(msg)) {
          setError(
            "Camera permission denied. Allow camera access in your phone settings, then reopen the scanner.",
          );
        } else if (/NotFound/i.test(msg)) {
          setError("No camera found on this device.");
        } else if (/secure|https/i.test(msg)) {
          setError("Camera requires HTTPS — this page must be served over TLS.");
        } else {
          setError(`Couldn't open camera: ${msg}`);
        }
      }
    })();
    return () => {
      cancelled = true;
      try {
        controlsRef.current?.stop();
      } catch {
        /* noop */
      }
      controlsRef.current = null;
    };
  }, [open, onDecode]);

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <Drawer.Portal>
        <Drawer.Overlay
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.7)",
            zIndex: 60,
          }}
        />
        <Drawer.Content
          aria-describedby={undefined}
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 61,
            display: "flex",
            flexDirection: "column",
            maxHeight: "90dvh",
            background: "var(--bg, #0E1116)",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            boxShadow: "0 -12px 28px rgba(0,0,0,0.4)",
            color: "var(--text)",
            fontFamily: "var(--font-text)",
            outline: "none",
          }}
        >
          <Drawer.Title
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: "hidden",
              clip: "rect(0, 0, 0, 0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
          >
            Scan QR code
          </Drawer.Title>
          <div
            style={{
              padding: "10px 0 6px",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 40,
                height: 4,
                borderRadius: 999,
                background: "var(--hint-soft, rgba(255,255,255,0.32))",
                margin: "0 auto",
              }}
            />
          </div>
          <div
            style={{
              padding:
                "8px 18px max(22px, env(safe-area-inset-bottom)) 18px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 14,
            }}
          >
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: 18, fontWeight: 700 }}>Scan QR code</div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                style={{
                  all: "unset",
                  width: 32,
                  height: 32,
                  borderRadius: 999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "var(--hint)",
                  fontSize: 18,
                  background: "var(--bg-2, #161A21)",
                }}
              >
                ✕
              </button>
            </div>
            {error ? (
              <div
                style={{
                  fontSize: 13,
                  color: "var(--danger, #FF3B30)",
                  textAlign: "center",
                  padding: "32px 16px",
                  lineHeight: 1.5,
                }}
              >
                {error}
              </div>
            ) : (
              <div
                style={{
                  width: "100%",
                  aspectRatio: "1 / 1",
                  borderRadius: 14,
                  overflow: "hidden",
                  background: "#000",
                  position: "relative",
                  maxWidth: 360,
                }}
              >
                <video
                  ref={videoRef}
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
                {/* Soft viewfinder overlay — corner brackets to guide the
                    user where to point. Cosmetic; the decoder reads the
                    full frame. */}
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 28,
                    border: "2px solid rgba(255,255,255,0.55)",
                    borderRadius: 16,
                    boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
                  }}
                />
              </div>
            )}
            <div
              style={{
                fontSize: 12,
                color: "var(--hint)",
                textAlign: "center",
                lineHeight: 1.5,
                maxWidth: 280,
              }}
            >
              Point at a Zcash address QR code. Detection happens on
              device — nothing leaves the camera frame.
            </div>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

/** Strip a ZIP-321 URI down to its address. Leaves bare addresses alone. */
export function extractAddressFromScan(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.toLowerCase().startsWith("zcash:")) {
    const after = trimmed.slice("zcash:".length);
    const qIdx = after.indexOf("?");
    return qIdx >= 0 ? after.slice(0, qIdx) : after;
  }
  return trimmed;
}
